import { MouseEventHandler, useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { UserContext, UserContextType } from '../../UserContext'
import { useMutate } from 'restful-react'
import { getCurrentIanaTimezone } from '../../utils'
import ScheduleSessionSuccessPage from './ScheduleSessionSuccessPage'
import useRootSnackbar from '../../services/useRootSnackbar'
import { AppContext, AppContextType, ScheduledSessionOfUser } from '../../AppContext'
import ElectronIPC from '../../services/ElectronIPC'
import { ApiCreateRoomResponse } from '../../RoomContext'
import {
    addTimeSelection,
    createScheduledSession,
    CreateSessionFormData,
    generateDefaultSessionName,
    generateTimeSelectionOptions,
    mkInitialCollaboratorUsernames,
    mkInitialEndTimeSelection,
    mkInitialStartDate,
    mkInitialStartTimeSelection,
    RepeatRule,
    TimeSelection,
    toFullStartAndEndTimes,
    toRRule,
    toRruleDate,
    toScheduledRrulePatchKey,
} from './ScheduleSessionLogic'
import CalendarSelection from './CalendarSelection'
import ScheduleSessionRecurrenceSection from './ScheduleSessionRecurrenceSection'
import MDatePicker from '../../UIComponents/MDatePicker'
import moment from 'moment'
import TzSelector from '../../UIComponents/TzSelector'
import { RRuleSet, rrulestr } from 'rrule'
import _ from 'lodash'
import { TextInput } from '@/UIComponents2/TextInput'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { Alert } from '@/UIComponents2/Alert'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import CollaboratorSelector from '@/components/CollaboratorSelector/CollaboratorSelector'
import { frontpageReloadTrigger } from '../DashboardPage/internal/useFrontpage'
import { cn } from '@/utlis/cn'

const timeSelectionOptions = generateTimeSelectionOptions()

export default function ScheduleSessionPage({
    editingSession,
    viewSession,
    isSingleInSeries,
    onClose,
}: {
    editingSession?: ScheduledSessionOfUser
    viewSession?: ScheduledSessionOfUser
    isSingleInSeries?: boolean
    onClose: () => void
}) {
    const { t } = useTranslation()

    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state } = useContext(UserContext) as UserContextType

    const username = state?.profile?.username || ''
    const isPasswordVisible = true

    const [sessionName, setSessionName] = useState(editingSession?.name || generateDefaultSessionName(username))
    const [collaboratorUsernames, set_collaboratorUsernames] = useState<string[]>(
        mkInitialCollaboratorUsernames(editingSession)
    )
    const suggestedCollaborators = useMemo(() => {
        const users = [...(state.profile?.knownUsers ?? [])]
        users.reverse()
        return users
    }, [state.profile])

    const [startDate, setStartDate] = useState<Date>(mkInitialStartDate(editingSession))
    const [startTimeSelection, setStartTimeSelection] = useState<TimeSelection>(
        mkInitialStartTimeSelection(editingSession)
    )
    const [endTimeSelection, setEndTimeSelection] = useState<TimeSelection>(mkInitialEndTimeSelection(editingSession))
    const [timeZoneCode, setTimeZoneCode] = useState(editingSession?.scheduledTimeZone || getCurrentIanaTimezone())
    const [password, set_password] = useState<string>(editingSession?.password || '')
    const [isPasswordEnabled, set_isPasswordEnabled] = useState<boolean>(!!editingSession?.password)
    const [cloudRecordingEnabled, set_isCloudRecordingEnabled] = useState<boolean>(
        editingSession?.cloudRecordingEnabled ?? false
    )

    const [repeatRule, setRepeatRule] = useState<RepeatRule>(
        (() => {
            const r = editingSession?.scheduledRrule?.origOptions || new RepeatRule()
            if (editingSession?.scheduledTimeZone) {
                r.tzid = editingSession.scheduledTimeZone
            }
            return r
        })()
    )

    // original versions of the above fields, to detect delta
    const [origStartDate] = useState<Date>(startDate)
    const [origStartTimeSelection] = useState<TimeSelection>(startTimeSelection)
    const [origEndTimeSelection] = useState<TimeSelection>(endTimeSelection)
    const [origTimeZoneCode] = useState(timeZoneCode)
    const [origRepeatRule] = useState<RepeatRule>(repeatRule)

    const [postResult, set_postResult] = useState<ApiCreateRoomResponse | null>(null)

    const RootSnackbar = useRootSnackbar()

    const {
        mutate: createRoom,
        loading: createRoomLoading,
        error: createRoomError,
    } = useMutate({ verb: 'POST', path: `/v1/rooms` })

    const { mutate: updateRoomApi, loading: updateRoomApiLoading } = useMutate({
        verb: 'PATCH',
        path: ({ roomUrn }) => `/v1/rooms/${roomUrn}`,
        resolve: (response) => {
            frontpageReloadTrigger.trigger()
            onClose()
        },
    })

    useEffect(() => {
        if (createRoomError) {
            // @ts-ignore
            const message = `There was an error: ${createRoomError?.message}`
            RootSnackbar.show({ severity: 'error', message })
        }
    }, [createRoomError])

    const onCreateScheduledSession = async (formData: CreateSessionFormData) => {
        const [result, err] = await createScheduledSession({ formData, editingSession, createRoom })
        if (result) {
            set_postResult(result as ApiCreateRoomResponse)

            if (result.googleCalendarLink) {
                ElectronIPC.openLinkInBrowser(result.googleCalendarLink)
            }
        } else if (err) {
            const message = `There was an error while processing the request.`
            RootSnackbar.show({ severity: 'error', message })
        }
    }

    const [calendarCloserClickEv, setCalendarCloserClickEv] = useState<React.MouseEvent<HTMLDivElement, MouseEvent>>()

    const datePickerClickWorkaround: MouseEventHandler<HTMLDivElement> = (ev) => {
        setCalendarCloserClickEv(ev)
    }

    const onSubmit = () => {
        if (isSingleInSeries && editingSession?.scheduledRrule) {
            // remove this instance in the series (RRULE EXDATE),
            // and patch { scheduledStartTime, and scheduledEndTime }.
            // Use `ruleSet.rdate(getDatetime(patchData.eventAt))`?
            const rrule = rrulestr(editingSession.scheduledRrule.icalStr)
            const ruleSet = new RRuleSet()
            ruleSet.rrule(rrule)

            // remove matching entry in `patches` and EXDATE if exists,
            // for handling update-to-an-update cases.

            const tzid = ruleSet.tzid() || getCurrentIanaTimezone()
            const exdate = toRruleDate(editingSession.scheduledStartTime, tzid)
            ruleSet.exdate(exdate)

            let icalStr = ruleSet.toString()

            const { scheduledStartTime, scheduledEndTime } = toFullStartAndEndTimes({
                startDate,
                startTimeSelection,
                endTimeSelection,
                timeZoneCode,
            })
            const patches = structuredClone(editingSession.scheduledRrule.patches || {})
            if (scheduledStartTime && scheduledEndTime) {
                const rdate = toRruleDate(scheduledStartTime, tzid)
                ruleSet.rdate(rdate)
                delete patches[toScheduledRrulePatchKey(editingSession.scheduledStartTime)]
                patches[toScheduledRrulePatchKey(scheduledStartTime)] = {
                    scheduledEndTime: moment(scheduledEndTime).toISOString(),
                }

                // remove EXDATE line that conflicts with our new startTime.
                {
                    icalStr = ruleSet.toString()
                    const match = moment(scheduledStartTime).format('YYYYMMDDTHHmmss')
                    icalStr = icalStr
                        .split('\n')
                        .filter((line) => !line.match(`^EXDATE.*:${match}`))
                        .join('\n')
                }
            }

            const scheduledRruleNext = {
                ...editingSession.scheduledRrule,
                icalStr,
                patches,
            }

            updateRoomApi(
                { scheduledRrule: scheduledRruleNext },
                {
                    pathParams: { roomUrn: editingSession.roomUrn },
                }
            )

            return
        }

        const { calendarIntegrationSelection } = appState.appSettings

        const rruleConvResult = toRRule(repeatRule)

        const rruleFieldsChanged = (() => {
            if (!editingSession) {
                return true
            }

            const b0 = moment(origStartDate).diff(startDate) === 0
            const b1 = origStartTimeSelection === startTimeSelection
            const b2 = origEndTimeSelection === endTimeSelection
            const b3 = origTimeZoneCode === timeZoneCode
            const b4 = _.isEqual(origRepeatRule, repeatRule)

            return !(b0 && b1 && b2 && b3 && b4)
        })()

        let formRrule = null
        if (rruleFieldsChanged && rruleConvResult) {
            formRrule = {
                origOptions: rruleConvResult.origOptions,
                icalStr: rruleConvResult.rrule.toString(),
                patches: {}, // clear existing patches
            }
        } else if (!rruleFieldsChanged && editingSession && editingSession.scheduledRrule) {
            formRrule = editingSession.scheduledRrule
        }

        onCreateScheduledSession({
            sessionName,
            username,
            startDate,
            startTimeSelection,
            endTimeSelection,
            timeZoneCode,
            collaboratorUsernames,
            password,
            calendarIntegrationSelection,
            cloudRecordingEnabled,
            rrule: formRrule,
        })
    }

    const startTimes = useMemo(() => {
        return [{ label: t('Start time') || '', value: '_undef', disabled: true }, ...timeSelectionOptions]
    }, [])
    const endTimes = useMemo(() => {
        return [{ label: t('End time') || '', value: '_undef', disabled: true }, ...timeSelectionOptions]
    }, [])

    if (postResult) {
        return (
            <ScheduleSessionSuccessPage
                title={'Your session has been scheduled!'}
                postResult={postResult}
                onClose={onClose}
            />
        )
    }

    if (viewSession) {
        return (
            <ScheduleSessionSuccessPage
                title={'Session Information'}
                postResult={{ room: viewSession }}
                onClose={onClose}
            />
        )
    }

    const title = editingSession ? t('Schedule Session Update') : t('Schedule Session')

    return (
        <div className="flex flex-col w-full h-full">
            <div className="flex flex-col w-full justify-between overflow-hidden" onClick={datePickerClickWorkaround}>
                <DialogTitle className="pl-6">{title}</DialogTitle>
                <DialogBody>
                    <div className="flex flex-col gap-3 pb-[1px]">
                        {/* session name */}
                        <div>
                            <div className="pb-2">{t('Session Name')}</div>
                            <TextInput
                                placeholder={'Session #0000'}
                                error={undefined}
                                helperText={undefined}
                                type="text"
                                onChange={setSessionName}
                            />
                        </div>

                        {/* invite collaborators */}
                        {!isSingleInSeries && (
                            <div>
                                <div className="pb-2">{t('Invite Collaborators')}</div>
                                <div className="flex items-center">
                                    <div className="grow">
                                        <CollaboratorSelector
                                            onChange={(collaborators: string[]) => {
                                                set_collaboratorUsernames(collaborators)
                                            }}
                                            suggestions={suggestedCollaborators.map((u) => u.username)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="w-full border-b border-white/10"></div>

                        <div className={cn('grid grid-cols-[30%_1fr] items-center')}>
                            <div>{t('Date')}</div>
                            <MDatePicker
                                date={startDate}
                                closerClickEv={calendarCloserClickEv}
                                onChange={setStartDate}
                            />
                        </div>

                        <div className={cn('grid grid-cols-[30%_1fr] items-center')}>
                            <div>{t('Time')}</div>
                            <div className="flex items-center gap-2">
                                <SimpleSelect
                                    value={startTimeSelection + ''}
                                    onChange={(value: string) => {
                                        const newStartTimeSelection = parseInt(value)
                                        if (endTimeSelection <= newStartTimeSelection) {
                                            if (endTimeSelection > startTimeSelection) {
                                                const dm = endTimeSelection - startTimeSelection
                                                const newEndTimeSelection = addTimeSelection(newStartTimeSelection, dm)
                                                setEndTimeSelection(newEndTimeSelection)
                                            }
                                        }
                                        setStartTimeSelection(newStartTimeSelection)
                                    }}
                                    items={startTimes}
                                />

                                <div>to</div>

                                <SimpleSelect
                                    value={endTimeSelection + ''}
                                    onChange={(value: string) => {
                                        setEndTimeSelection(parseInt(value))
                                    }}
                                    items={endTimes}
                                />

                                {endTimeSelection < startTimeSelection && <div>(next day)</div>}
                            </div>
                        </div>

                        {/* Time Zone */}
                        <div className={'grid grid-cols-[30%_1fr] items-center '}>
                            <div>{t('Time Zone')}</div>

                            <div className="flex">
                                <TzSelector
                                    value={timeZoneCode}
                                    disabled={isSingleInSeries}
                                    onChange={(code) => {
                                        setRepeatRule((s) => ({ ...s, tzid: code }))
                                        setTimeZoneCode(code)
                                    }}
                                />
                            </div>
                        </div>

                        {isSingleInSeries ? (
                            <>
                                <Alert className="mt-3" level="warning" variant="solid">
                                    {t('You are updating this one session in a recurring series.')}
                                </Alert>
                            </>
                        ) : (
                            <>
                                <ScheduleSessionRecurrenceSection
                                    startDate={startDate}
                                    startTimeSelection={startTimeSelection}
                                    repeatRule={repeatRule}
                                    timeZoneCode={timeZoneCode}
                                    isModify={!!editingSession}
                                    calendarCloserClickEv={calendarCloserClickEv}
                                    setRepeatRule={setRepeatRule}
                                />

                                <div className="w-full border-b border-white/10"></div>

                                <CalendarSelection />

                                <div className="w-full border-b border-white/10"></div>

                                {/* password */}
                                <div className={cn('grid grid-cols-[30%_1fr] items-center')}>
                                    <div>{t('Session Password')}</div>

                                    <div className="flex w-full">
                                        <Checkbox
                                            className="mr-3"
                                            checked={isPasswordEnabled}
                                            onChange={() => {
                                                set_isPasswordEnabled(!isPasswordEnabled)
                                                if (!!isPasswordEnabled) {
                                                    set_password('')
                                                }
                                            }}
                                        />
                                        <TextInput
                                            placeholder={t('Password') || ''}
                                            type={isPasswordVisible ? 'text' : 'password'}
                                            value={password}
                                            onChange={(value: string) => {
                                                set_password(value)
                                                set_isPasswordEnabled(true)
                                            }}
                                        />
                                    </div>
                                </div>

                                {state.profile?.policy?.canUseCloudRecording && (
                                    <>
                                        <div className="w-full border-b border-white/10"></div>
                                        <div className={'flex flex-col gap-2'}>
                                            <Checkbox
                                                label={t('Auto-Start Cloud Recording When Host Joins') || ''}
                                                className="flex gap-3"
                                                checked={cloudRecordingEnabled}
                                                onChange={() => {
                                                    set_isCloudRecordingEnabled(!cloudRecordingEnabled)
                                                }}
                                            />
                                            <p className="text-sm text-neutral-500 pl-6">
                                                <Trans
                                                    i18nKey="The maximum recording length is <0>2 hours</0>. Recording will stop once the limit is reached."
                                                    components={[<strong />]}
                                                    t={t}
                                                />
                                            </p>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </DialogBody>
            </div>

            <DialogActionButtonsSection
                leftButtonText={t('Cancel')}
                rightButtonText={editingSession ? t('Update') : t('Schedule')}
                isLeftButtonDisabled={createRoomLoading}
                isRightButtonDisabled={createRoomLoading}
                onLeftButtonClicked={onClose}
                onRightButtonClicked={onSubmit}
            />
        </div>
    )
}
